import { TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { getChargeSplitPayees } from 'services'
import { Input2, SearchableSelect } from 'stories/components'
import { removeComma } from 'utils'

/**
 * One share of a collected fee paid to somebody other than the loan's investors — the broker /
 * servicer split TMO calls a fee split. Mirrors the backend FeeSplitLine.
 *
 * A `Party` line names a role on the loan's party screen (`partyRole`) rather than a fixed payee,
 * so the share follows whoever holds that role when the money is actually collected.
 */
export interface IFeeSplitLine {
  payeeType: string
  payeeRefId?: number | null
  payeeName?: string
  partyRole?: string
  percent: number | string
}

/** Everyone a split can be paid to, as returned by GET /loan/chargeSplitPayees. */
export interface IFeeSplitPayees {
  /** `name` is who a company share is actually paid out to — the company's own payee record. */
  company?: { payeeType: string; label: string; name?: string }
  parties?: Array<{ payeeType: string; partyRole: string; label: string; name: string }>
  vendors?: Array<{ payeeType: string; payeeRefId: number; name: string }>
  participants?: Array<{ payeeType: string; payeeRefId: number; name: string }>
}

const emptyLine = (): IFeeSplitLine => ({ payeeType: '', payeeRefId: null, payeeName: '', percent: '' })

/** Stable key for a payee, used as the select's value. */
const keyOf = (line: IFeeSplitLine): string => {
  if (!line?.payeeType) return ''
  if (line.payeeType === 'Party') return `Party:${line.partyRole || ''}`
  if (line.payeeRefId) return `${line.payeeType}:${line.payeeRefId}`
  return `${line.payeeType}:`
}

/** The select's options, keyed the same way, so a saved line shows the payee it was set to. */
export const feeSplitPayeeOptions = (payees: IFeeSplitPayees): Record<string, string> => {
  const options: Record<string, string> = {}
  if (payees?.company)
    options[`${payees.company.payeeType}:`] = payees.company.name
      ? `${payees.company.label} — ${payees.company.name}`
      : payees.company.label
  ;(payees?.parties || []).forEach((p) => {
    // A role nobody holds on this loan is still offered: the split is configured before the party
    // screen is necessarily filled in, and it resolves when the fee is collected.
    options[`Party:${p.partyRole}`] = p.name ? `${p.label} — ${p.name}` : `${p.label} (party)`
  })
  ;(payees?.participants || []).forEach((p) => {
    options[`Participant:${p.payeeRefId}`] = `${p.name} (investor)`
  })
  ;(payees?.vendors || []).forEach((v) => {
    options[`Vendor:${v.payeeRefId}`] = `${v.name} (vendor)`
  })
  return options
}

/**
 * Turn a picked option back into a split line's payee fields. The name stored is the payee's real
 * name, never the picker's label: the server re-resolves it when the fee is collected, but the name
 * is what a grid summary shows, so "(vendor)" must not end up in it.
 */
const lineFromKey = (key: string, payees: IFeeSplitPayees): Partial<IFeeSplitLine> => {
  const [payeeType, ref] = String(key || '').split(':')
  if (payeeType === 'Party') {
    const party = (payees?.parties || []).find((p) => p.partyRole === ref)
    return { payeeType, partyRole: ref, payeeRefId: null, payeeName: party?.name || ref }
  }
  if (payeeType === 'Company') return { payeeType, partyRole: undefined, payeeRefId: null, payeeName: 'Company' }
  const refId = Number(ref) || null
  const match = [...(payees?.vendors || []), ...(payees?.participants || [])].find(
    (p) => p.payeeType === payeeType && p.payeeRefId === refId,
  )
  return { payeeType, partyRole: undefined, payeeRefId: refId, payeeName: match?.name || '' }
}

/** Percent of the fee the investors keep — whatever the split lines do not claim. */
export const investorShare = (splits: IFeeSplitLine[] = []): number => {
  const taken = (splits || []).reduce((sum, s) => sum + Number(removeComma(s?.percent ?? 0) || 0), 0)
  return Math.max(0, Number((100 - taken).toFixed(2)))
}

/**
 * One-line summary of a split for a grid cell. Empty when nothing is split away.
 *
 * A `Company` line's stored `payeeName` is only a label and is never who gets paid: the server
 * re-resolves a company share to the company's own payee record when the fee is collected. So the
 * stored name is deliberately ignored here — showing it names a payee the cheque is not drawn to,
 * which is how a converted split carrying the prior servicer's payee came to contradict the check
 * queue. Pass `companyName` (from `useFeeSplitPayees().company.name`) to show the real payee.
 */
export const feeSplitSummary = (splits: IFeeSplitLine[] = [], companyName?: string): string =>
  (splits || [])
    .filter((s) => Number(removeComma(s?.percent ?? 0) || 0) > 0)
    .map((s) => {
      const who =
        s.payeeType === 'Company'
          ? companyName || 'Company (Servicer)'
          : s.partyRole || s.payeeName || s.payeeType
      return `${who} ${Number(removeComma(s.percent))}%`
    })
    .join(', ')

/** Fetch the payee list once. Pass a loan number from a charge, nothing from the category admin. */
export const useFeeSplitPayees = (loanNumber?: number) => {
  const [payees, setPayees] = useState<IFeeSplitPayees>({})
  useEffect(() => {
    getChargeSplitPayees(loanNumber)
      .then((res: any) => setPayees(res || {}))
      .catch(() => {})
  }, [loanNumber])
  return payees
}

/**
 * Add/edit table of fee-split lines: who is paid out of a fee, and what percent of it. Used on the
 * charge modal (this charge's split) and on the Charge Categories admin (the default a new charge
 * in that category starts with).
 *
 * Whatever the lines do not claim stays with the loan's investors, which is shown as its own row so
 * a half-configured split is obvious rather than silently short.
 */
export const FeeSplitEditor = ({
  value,
  onChange,
  payees,
  disabled = false,
  title = 'Fee Split',
  hint = 'When this fee is collected, each payee below is queued for payout with their share of it. The rest stays with the loan’s investors.',
  // Who keeps what the lines do not claim. The investors do on a charge, because the fee was
  // collected on their loan payment; on a construction draw's inspection fee it is the lender, who
  // funded the fee out of the reserve. Naming the wrong one makes a half-set split read as fine.
  remainderLabel = 'Investors keep:',
  emptyText = 'Not split — the whole fee goes to the investors.',
}: {
  value: IFeeSplitLine[]
  onChange: (rows: IFeeSplitLine[]) => void
  payees: IFeeSplitPayees
  disabled?: boolean
  title?: string
  hint?: string
  remainderLabel?: string
  emptyText?: string
}) => {
  const rows = Array.isArray(value) ? value : []
  const options = feeSplitPayeeOptions(payees)
  const remaining = investorShare(rows)

  const update = (index: number, patch: Partial<IFeeSplitLine>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <p className="font-semibold text-sm">{title}</p>
        {!disabled && (
          <span
            className="cursor-pointer text-shade-blue hover:underline text-sm"
            onClick={() => onChange([...rows, emptyLine()])}
          >
            + Add payee
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 italic">{emptyText}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row, index) => (
            <div className="grid grid-cols-12 gap-2 items-end" key={index}>
              <div className="col-span-8">
                <SearchableSelect
                  id={`fee-split-payee-${index}`}
                  title="Paid to"
                  value={keyOf(row)}
                  options={options}
                  hasDefaultOption
                  defaultOptionText="Select a payee"
                  disabled={disabled}
                  error={!row.payeeType && Number(removeComma(row.percent ?? 0) || 0) > 0 ? 'Choose who is paid' : ''}
                  onChange={(key: string) => update(index, lineFromKey(key, payees))}
                />
              </div>
              <div className="col-span-3">
                <Input2
                  title="Share"
                  type="thousandSep"
                  prefix="%"
                  value={String(row.percent ?? '')}
                  disabled={disabled}
                  onChange={(v: string) => update(index, { percent: v })}
                />
              </div>
              <div className="col-span-1 flex justify-center pb-2">
                {!disabled && (
                  <TrashIcon
                    className="w-5 h-5 text-red-500 cursor-pointer"
                    onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex justify-end mt-2 text-sm font-medium">
          <span className="text-gray-500 mr-2">{remainderLabel}</span>
          <span>{remaining}%</span>
        </div>
      )}
    </div>
  )
}
